import { useState, useEffect } from 'react';
import { speechSupported } from '../utils/voiceUtils';

const PERMISSION_KEY = 'voice_permission_asked';

function useMicrophonePermission() {
  const [granted, setGranted] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    // Check if permission was already asked
    const alreadyAsked = localStorage.getItem(PERMISSION_KEY);

    if (!speechSupported) {
      setDenied(true);
      return;
    }

    if (alreadyAsked) {
      // Permission was asked before; check current state via navigator.permissions
      if ('permissions' in navigator) {
        navigator.permissions.query({ name: 'microphone' }).then((result) => {
          if (result.state === 'granted') {
            setGranted(true);
            setDenied(false);
          } else if (result.state === 'denied') {
            setGranted(false);
            setDenied(true);
          } else {
            // prompt state (user hasn't decided yet)
            setGranted(null);
          }
        }).catch(() => {
          // permissions API not available; grant status unknown
          setGranted(null);
        });
      }
    } else {
      // Permission not asked yet; mark as "to be asked"
      setGranted(null);
      setDenied(false);
    }
  }, []);

  const requestPermission = async () => {
    if (!speechSupported) {
      setDenied(true);
      return;
    }

    try {
      // Attempt to request permission by trying to use the API
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop()); // stop the stream immediately
      setGranted(true);
      setDenied(false);
      localStorage.setItem(PERMISSION_KEY, 'true');
    } catch (error) {
      if (error.name === 'NotAllowedError' || error.name === 'NotFoundError') {
        setGranted(false);
        setDenied(true);
      } else {
        // Other errors (NotSupportedError, etc.)
        setGranted(false);
        setDenied(true);
      }
      localStorage.setItem(PERMISSION_KEY, 'true');
    }
  };

  return {
    supported: speechSupported,
    granted,
    denied,
    requestPermission,
  };
}

export default useMicrophonePermission;
